export const appviaBrandColors = {
  navy: {
    deep: "#022859", // Primary brand navy
    dark: "#0c0d49", // Dark navy
    medium: "#14358a", // Medium blue
  },
  blue: {
    bright: "#1565C0", // Darker blue (updated)
    sky: "#0D47A1", // Even darker blue for hover (updated)
    light: "#0b3b88", // Light blue
    veryLight: "#e8f3fe", // Very light blue background
  },
  purple: {
    brand: "#6a36ff", // Brand purple
    light: "#ac5fe6", // Light purple/magenta
    soft: "#9f7fdc", // Soft purple
  },
  gray: {
    50: "#f8fafc", // Very light background
    100: "#f1f5f9", // Light divider
    200: "#e2e8f1", // Light border
    300: "#dee2e5", // Border
    400: "#94a3b8", // Light gray text
    500: "#758696", // Gray-blue
    600: "#475569", // Medium gray text
    700: "#334155", // Dark gray
    800: "#1e293b", // Very dark
    900: "#0f172a", // Very dark blue-gray
  },
  semantic: {
    success: "#059669", // Green
    warning: "#d97706", // Orange
    error: "#dc2626", // Red
    info: "#1565C0", // Blue (same as bright - updated)
  },
  white: "#ffffff",
  black: "#000000",
} as const;

export const lightTheme = {
  background: {
    default: "#f0f4f8", // Pale blue-gray background for consistency
    paper: appviaBrandColors.white,
  },

  primary: {
    main: appviaBrandColors.navy.deep,
    light: appviaBrandColors.blue.bright,
    dark: appviaBrandColors.navy.dark,
    contrastText: appviaBrandColors.white,
  },

  secondary: {
    main: appviaBrandColors.purple.brand,
    light: appviaBrandColors.purple.light,
    dark: appviaBrandColors.purple.soft,
    contrastText: appviaBrandColors.white,
  },

  text: {
    primary: appviaBrandColors.gray[900],
    secondary: appviaBrandColors.gray[600],
    disabled: appviaBrandColors.gray[400],
  },

  error: {
    main: appviaBrandColors.semantic.error,
    light: "#fef2f2",
    dark: "#991b1b",
    contrastText: appviaBrandColors.white,
  },

  warning: {
    main: appviaBrandColors.semantic.warning,
    light: "#fffbeb",
    dark: "#92400e",
    contrastText: appviaBrandColors.white,
  },

  success: {
    main: appviaBrandColors.semantic.success,
    light: "#ecfdf5",
    dark: "#064e3b",
    contrastText: appviaBrandColors.white,
  },

  info: {
    main: appviaBrandColors.semantic.info,
    light: appviaBrandColors.blue.veryLight,
    dark: appviaBrandColors.navy.medium,
    contrastText: appviaBrandColors.white,
  },

  divider: appviaBrandColors.gray[100],

  action: {
    active: appviaBrandColors.gray[600],
    hover: "rgba(2, 40, 89, 0.04)",
    selected: "rgba(2, 40, 89, 0.08)",
    disabled: appviaBrandColors.gray[400],
    disabledBackground: appviaBrandColors.gray[200],
  },

  navigation: {
    background: appviaBrandColors.navy.deep,
    indicator: appviaBrandColors.blue.bright,
    color: appviaBrandColors.blue.veryLight,
    selectedColor: appviaBrandColors.white,
  },
};

export const darkTheme = {
  background: {
    default: "#0a0f1b", // Very dark navy
    paper: "#1a2332", // Dark navy card
  },

  primary: {
    main: appviaBrandColors.blue.bright,
    light: appviaBrandColors.blue.sky,
    dark: appviaBrandColors.navy.medium,
    contrastText: appviaBrandColors.white,
  },

  secondary: {
    main: appviaBrandColors.purple.light,
    light: "#d4a5ff",
    dark: appviaBrandColors.purple.brand,
    contrastText: "#0a0f1b",
  },

  text: {
    primary: "#f1f5f9",
    secondary: "#cbd5e1",
    disabled: "#64748b",
  },

  error: {
    main: "#ef4444",
    light: "#fca5a5",
    dark: "#dc2626",
    contrastText: appviaBrandColors.white,
  },

  warning: {
    main: "#f59e0b",
    light: "#fcd34d",
    dark: "#d97706",
    contrastText: "#0a0f1b",
  },

  success: {
    main: "#10b981",
    light: "#6ee7b7",
    dark: "#059669",
    contrastText: appviaBrandColors.white,
  },

  info: {
    main: appviaBrandColors.blue.sky,
    light: "#93c5fd",
    dark: appviaBrandColors.blue.bright,
    contrastText: appviaBrandColors.white,
  },

  divider: "#334155",

  action: {
    active: "#cbd5e1",
    hover: "rgba(241, 245, 249, 0.08)",
    selected: "rgba(241, 245, 249, 0.12)",
    disabled: "#64748b",
    disabledBackground: "#1e293b",
  },

  navigation: {
    background: "#0a0f1b",
    indicator: appviaBrandColors.blue.bright,
    color: "#e2e8f0",
    selectedColor: appviaBrandColors.white,
  },
};

export const typography = {
  fontFamily:
    '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
  monoFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
  htmlFontSize: 16,
};

export const spacing = {
  unit: 4,
  radius: {
    sm: 4,
    md: 6,
    lg: 8,
    xl: 12,
  },
};

export const shadows = {
  light: [
    "none",
    "0px 1px 2px 0px rgba(2, 40, 89, 0.05)",
    "0px 1px 3px 0px rgba(2, 40, 89, 0.1), 0px 1px 2px 0px rgba(2, 40, 89, 0.06)",
    "0px 2px 4px 0px rgba(2, 40, 89, 0.06), 0px 1px 2px 0px rgba(2, 40, 89, 0.08)",
    "0px 4px 6px -1px rgba(2, 40, 89, 0.1), 0px 2px 4px -1px rgba(2, 40, 89, 0.06)",
    "0px 10px 15px -3px rgba(2, 40, 89, 0.1), 0px 4px 6px -2px rgba(2, 40, 89, 0.05)",
    "0px 20px 25px -5px rgba(2, 40, 89, 0.1), 0px 10px 10px -5px rgba(2, 40, 89, 0.04)",
    "0px 25px 50px -12px rgba(2, 40, 89, 0.25)",
  ],
  dark: [
    "none",
    "0px 1px 2px 0px rgba(0, 0, 0, 0.2)",
    "0px 1px 3px 0px rgba(0, 0, 0, 0.3)",
    "0px 2px 4px 0px rgba(0, 0, 0, 0.3)",
    "0px 4px 6px -1px rgba(0, 0, 0, 0.4)",
    "0px 10px 15px -3px rgba(0, 0, 0, 0.4)",
    "0px 20px 25px -5px rgba(0, 0, 0, 0.5)",
    "0px 25px 50px -12px rgba(0, 0, 0, 0.6)",
  ],
};

export const chartColors = {
  light: [
    appviaBrandColors.navy.deep,
    appviaBrandColors.blue.bright,
    appviaBrandColors.purple.brand,
    appviaBrandColors.purple.light,
    appviaBrandColors.purple.soft,
    appviaBrandColors.blue.sky,
    appviaBrandColors.blue.light,
  ],
  dark: [
    appviaBrandColors.blue.bright,
    appviaBrandColors.purple.light,
    appviaBrandColors.blue.sky,
    appviaBrandColors.purple.soft,
    "#93c5fd", // Light blue
    "#d4a5ff", // Light purple
    "#6ee7b7", // Light green
  ],
};

export const getThemeConfig = (mode: "light" | "dark" = "light") => {
  const colors = mode === "light" ? lightTheme : darkTheme;
  const chartPalette = mode === "light" ? chartColors.light : chartColors.dark;
  const shadowPalette = mode === "light" ? shadows.light : shadows.dark;

  return {
    colors,
    typography,
    spacing,
    shadows: shadowPalette,
    charts: chartPalette,
    mode,
  };
};
